import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { triggerShipmentExpiry, type TriggerExpiryResponse } from '../../api/endpoints/admin';
import styles from './SettingsView.module.css';
import team from './OpsTeamTab.module.css';
import { useOpsUser } from '../ownership/opsUser';
import { DetentionFreeHoursField } from './DetentionFreeHoursField';

/**
 * Operations tab — the detention free time (dnm-work#165; managers edit) and admin-only actions for
 * triggering background jobs manually (shipment auto-expiry).
 */
export function OperationsTab() {
  const { t } = useTranslation();
  const { isManager } = useOpsUser();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TriggerExpiryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTriggerExpiry = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await triggerShipmentExpiry();
      setResult(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to trigger expiry job';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{t('stops.settings.title')}</h3>
        <p className={styles.cardHint}>{t('stops.settings.cardHint')}</p>
        <div className={team.grid}>
          <DetentionFreeHoursField canEdit={isManager} />
        </div>
      </div>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{t('settings.operations.title')}</h3>
        <p className={styles.cardHint}>
          <Trans i18nKey="settings.operations.hint" components={{ strong: <strong /> }} />
        </p>

        <div className={styles.actions} style={{ justifyContent: 'flex-start' }}>
          <button
            type="button"
            className={styles.submit}
            disabled={loading}
            onClick={handleTriggerExpiry}
          >
            {loading ? 'Running…' : 'Trigger expiry now'}
          </button>
        </div>

        {result && (
          <div
            className={result.expiredCount > 0 ? styles.resultSuccess : styles.resultInfo}
            style={{ marginTop: 14 }}
          >
            <strong>{result.expiredCount}</strong> {t('settings.operations.shipmentsExpired')}
            {result.expiredCount > 0 && ` (pickup date > ${result.graceDays} days ago)`}
            {result.expiredCount === 0 && ' — no shipments eligible at this time'}
          </div>
        )}

        {error && (
          <div className={styles.error} style={{ marginTop: 14 }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}
